import React, { useEffect, useRef, useState } from "react";
import {
  Loader2, ArrowRight, ArrowLeft, Check, CheckCircle2, MapPin, Phone, Mail,
  Briefcase, User as UserIcon, Building2, UserPlus, Send, PartyPopper, Headset, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { companyOnboardingApi, invitesApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

// Ordered info cards (one question per card).
const INFO = ["address", "phone", "email", "position", "name"];

const inputCls =
  "w-full h-11 px-3.5 rounded-xl border border-slate-200 text-[15px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400";

export default function CompanyOnboarding() {
  const { user, hasModule, refreshMe } = useAuth();
  const [visible, setVisible] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [seatsAvailable, setSeatsAvailable] = useState(0);
  const [screen, setScreen] = useState("address");
  const [anim, setAnim] = useState("in"); // in | out | enter
  const dirRef = useRef(1);
  const [busy, setBusy] = useState(false);

  // company + owner profile
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // invites
  const [invName, setInvName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("dispatcher");
  const [sentCount, setSentCount] = useState(0);
  const didInitRef = useRef(false);

  useEffect(() => {
    if (!user || user === false || user.role === "super_admin" || user.role === "technician" || !hasModule("onboarding_checklists")) {
      if (!didInitRef.current) setVisible(false);
      return;
    }
    // Initialize ONCE per session. refreshMe() later mutates `user` (after
    // completing the info steps); without this guard the effect would re-fetch
    // state (now needed=false) and close the wizard before the invite step shows.
    if (didInitRef.current) return;
    didInitRef.current = true;
    let cancelled = false;
    companyOnboardingApi
      .state()
      .then((s) => {
        if (cancelled) return;
        if (!s.needed) {
          setVisible(false);
          return;
        }
        setCompanyName(s.companyName || "");
        setSeatsAvailable(s.seatsAvailable || 0);
        setAddress(s.company?.address || "");
        setPhone(s.company?.phone || "");
        setEmail(s.company?.email || "");
        setPosition(s.profile?.jobTitle || "");
        setFirstName(s.profile?.firstName || "");
        setLastName(s.profile?.lastName || "");
        setScreen("address");
        setAnim("in");
        setVisible(true);
      })
      .catch(() => setVisible(false));
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!visible) return null;

  const goTo = (next, dir) => {
    dirRef.current = dir;
    setAnim("out");
    setTimeout(() => {
      setScreen(next);
      setAnim("enter");
      requestAnimationFrame(() => requestAnimationFrame(() => setAnim("in")));
    }, 220);
  };

  const infoIdx = INFO.indexOf(screen);
  const isInfo = infoIdx !== -1;

  const requireVal = (val, msg) => {
    if (!String(val || "").trim()) {
      toast.error(msg);
      return false;
    }
    return true;
  };
  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

  const nextInfo = () => {
    if (screen === "address" && !requireVal(address, "Please enter your company address")) return;
    if (screen === "phone" && !requireVal(phone, "Please enter your company phone")) return;
    if (screen === "email") {
      if (!requireVal(email, "Please enter your company email")) return;
      if (!emailValid(email)) return toast.error("Please enter a valid email address");
    }
    if (screen === "position" && !requireVal(position, "Please enter your position / title")) return;
    const ni = infoIdx + 1;
    if (ni < INFO.length) goTo(INFO[ni], 1);
  };

  const backInfo = () => {
    if (infoIdx > 0) goTo(INFO[infoIdx - 1], -1);
  };

  const onSave = async () => {
    if (!requireVal(firstName, "Please enter your first name")) return;
    if (!requireVal(lastName, "Please enter your last name")) return;
    setBusy(true);
    try {
      await companyOnboardingApi.save({
        address, phone, email, jobTitle: position, firstName, lastName,
      });
      await companyOnboardingApi.complete();
      await refreshMe();
      goTo(seatsAvailable > 0 ? "invitePrompt" : "done", 1);
    } catch (_) {
      toast.error("Could not save — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    if (!requireVal(invEmail, "Enter your teammate's email")) return;
    if (!emailValid(invEmail)) return toast.error("Enter a valid email address");
    setBusy(true);
    try {
      await invitesApi.create({
        email: invEmail.trim().toLowerCase(),
        role: invRole,
        name: invName.trim() || undefined,
      });
      const n = sentCount + 1;
      setSentCount(n);
      toast.success(`Invite sent to ${invEmail.trim()}`);
      setInvName("");
      setInvEmail("");
      setInvRole("dispatcher");
      if (n >= seatsAvailable) goTo("done", 1);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send the invite.");
    } finally {
      setBusy(false);
    }
  };

  const finish = () => setVisible(false);

  // Slide/fade animation classes for the card.
  const cardAnim = (() => {
    const dur = anim === "enter" ? "duration-0" : "duration-300";
    let t = "translate-x-0 opacity-100";
    if (anim === "out") t = dirRef.current > 0 ? "-translate-x-20 opacity-0" : "translate-x-20 opacity-0";
    else if (anim === "enter") t = dirRef.current > 0 ? "translate-x-20 opacity-0" : "-translate-x-20 opacity-0";
    return `transition-all ease-out ${dur} ${t}`;
  })();

  const progressPct = isInfo ? Math.round(((infoIdx + 1) / INFO.length) * 100) : 100;

  return (
    <div
      data-testid="company-onboarding"
      className="fixed inset-0 z-[210] flex items-center justify-center bg-[#070B16]/85 backdrop-blur-md p-4"
    >
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header — company name shown on every card */}
        <div className="relative px-7 pt-6 pb-5 bg-gradient-to-br from-[#1D4ED8] via-[#2563EB] to-[#0EA5E9]">
          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }}
          />
          <div className="relative flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
              <Building2 className="h-6 w-6 text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="text-blue-100 text-[11px] uppercase tracking-widest">Setting up</div>
              <div className="text-white font-bold text-[19px] leading-tight truncate" data-testid="co-company-name">
                {companyName}
              </div>
            </div>
          </div>
          <div className="relative mt-4 h-1.5 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full bg-white transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="co-progress" />
          </div>
        </div>

        {/* Animated card body */}
        <div className="px-7 py-7 min-h-[260px] overflow-hidden">
          <div className={cardAnim} data-testid={`co-step-${screen}`}>
            {screen === "address" && (
              <StepField
                icon={MapPin}
                title="What's your company address?"
                sub="Shown on your estimates, invoices and documents."
              >
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  data-testid="co-input-address"
                  placeholder="123 Main St, City, State ZIP"
                  className={inputCls}
                  autoFocus
                />
              </StepField>
            )}

            {screen === "phone" && (
              <StepField icon={Phone} title="Company phone number" sub="How customers reach your business.">
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="co-input-phone"
                  placeholder="(555) 123-4567"
                  className={inputCls}
                  autoFocus
                />
              </StepField>
            )}

            {screen === "email" && (
              <StepField
                icon={Mail}
                title="Company email address"
                sub="Used as the sender for the emails you send from ServicePro. This can differ from your login email — edit it anytime in Company Settings."
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="co-input-email"
                  placeholder="hello@yourcompany.com"
                  className={inputCls}
                  autoFocus
                />
              </StepField>
            )}

            {screen === "position" && (
              <StepField icon={Briefcase} title="Your position / title" sub="e.g. Owner, Office Manager, General Manager.">
                <input
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  data-testid="co-input-position"
                  placeholder="Owner"
                  className={inputCls}
                  autoFocus
                />
              </StepField>
            )}

            {screen === "name" && (
              <StepField icon={UserIcon} title="What's your name?" sub="So your team and customers know who they're talking to.">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    data-testid="co-input-first"
                    placeholder="First name"
                    className={inputCls}
                    autoFocus
                  />
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    data-testid="co-input-last"
                    placeholder="Last name"
                    className={inputCls}
                  />
                </div>
              </StepField>
            )}

            {screen === "invitePrompt" && (
              <div className="text-center py-3">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                  <UserPlus className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-[19px] font-bold text-slate-900 mb-1.5">Invite your team</h3>
                <p className="text-[13.5px] text-slate-500 leading-relaxed max-w-sm mx-auto">
                  Your plan includes {seatsAvailable} more free {seatsAvailable === 1 ? "seat" : "seats"} for your 14-day trial.
                  Invite a dispatcher or technician to get started together.
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => goTo("done", 1)}
                    data-testid="co-invite-no"
                    className="h-11 px-5 rounded-xl border border-slate-200 text-[14px] font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    Maybe later
                  </button>
                  <button
                    type="button"
                    onClick={() => goTo("inviteForm", 1)}
                    data-testid="co-invite-yes"
                    className="h-11 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold inline-flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" /> Yes, invite
                  </button>
                </div>
              </div>
            )}

            {screen === "inviteForm" && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-[17px] font-bold text-slate-900">Invite a teammate</h3>
                  <p className="text-[12.5px] text-slate-500 mt-0.5">
                    {Math.max(0, seatsAvailable - sentCount)} of {seatsAvailable} free {seatsAvailable === 1 ? "seat" : "seats"} remaining
                    {sentCount > 0 ? ` · ${sentCount} sent` : ""}
                  </p>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Name</label>
                  <input value={invName} onChange={(e) => setInvName(e.target.value)} data-testid="co-invite-name" placeholder="Teammate's name" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Email</label>
                  <input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} data-testid="co-invite-email" placeholder="teammate@yourcompany.com" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-2">Role</label>
                  <div className="grid grid-cols-2 gap-2.5">
                    <RolePick active={invRole === "dispatcher"} onClick={() => setInvRole("dispatcher")} icon={Headset} label="Dispatcher" desc="Schedule & manage" testid="co-invite-role-dispatcher" />
                    <RolePick active={invRole === "technician"} onClick={() => setInvRole("technician")} icon={Truck} label="Technician" desc="Field work" testid="co-invite-role-technician" />
                  </div>
                </div>
              </div>
            )}

            {screen === "done" && (
              <div className="text-center py-6">
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                  <PartyPopper className="h-9 w-9 text-emerald-500" />
                </div>
                <h3 className="text-[20px] font-bold text-slate-900 mb-1.5">You're all set!</h3>
                <p className="text-[13.5px] text-slate-500 max-w-xs mx-auto">
                  {companyName} is ready to go. Your 14-day free trial has started.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/60">
          <div>
            {isInfo && infoIdx > 0 && (
              <button type="button" onClick={backInfo} data-testid="co-back" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            {screen === "inviteForm" && (
              <button type="button" onClick={() => goTo("done", 1)} data-testid="co-invite-skip" className="text-[12.5px] font-medium text-slate-400 hover:text-slate-600">
                Skip for now
              </button>
            )}
          </div>
          <div>
            {isInfo && screen !== "name" && (
              <button type="button" onClick={nextInfo} data-testid="co-next" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold">
                Next <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {screen === "name" && (
              <button type="button" onClick={onSave} disabled={busy} data-testid="co-save" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] font-semibold disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
              </button>
            )}
            {screen === "inviteForm" && (
              <button type="button" onClick={sendInvite} disabled={busy} data-testid="co-invite-send" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send invite
              </button>
            )}
            {screen === "done" && (
              <button type="button" onClick={finish} data-testid="co-finish" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold">
                <CheckCircle2 className="h-4 w-4" /> Go to dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepField({ icon: Icon, title, sub, children }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-[17px] font-bold text-slate-900 leading-tight">{title}</h3>
          {sub && <p className="text-[12.5px] text-slate-500 leading-snug mt-1">{sub}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function RolePick({ active, onClick, icon: Icon, label, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
        active ? "border-blue-400 bg-blue-50/70 ring-2 ring-blue-500/20" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div>
        <div className="text-[13.5px] font-semibold text-slate-800">{label}</div>
        <div className="text-[11px] text-slate-500">{desc}</div>
      </div>
    </button>
  );
}
